import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { forceThemeColors } from '../utils/forceThemeColors';
import { applyFaviconToDocument } from '../utils/assetUrl';
import { useAuth } from './AuthContext';

interface SystemSettings {
  site: {
    name: string;
    description: string;
    logo: string;
    favicon: string;
    language: string;
    timezone: string;
    contact?: {
      email: string;
      phone: string;
      address: string;
      businessHours: string;
    };
    geo?: {
      latitude: string;
      longitude: string;
      showLocation?: boolean;
    };
  };
  email: {
    provider: string;
    sendgridApiKey: string;
    mailersendApiKey: string;
    smtpHost: string;
    smtpPort: number;
    smtpUser: string;
    smtpPassword: string;
    smtpSecure: boolean;
    fromEmail: string;
    fromName: string;
  };
  database: {
    backupEnabled: boolean;
    backupFrequency: string;
    retentionDays: number;
  };
  security: {
    sessionTimeout: number;
    maxLoginAttempts: number;
    passwordMinLength: number;
    twoFactorEnabled: boolean;
  };
  notifications: {
    emailNotifications: boolean;
    adminNotifications: boolean;
    userNotifications: boolean;
  };
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
    whatsapp?: string;
  };
  appearance: {
    primaryColor: string;
    secondaryColor: string;
    theme: string;
    customCSS: string;
  };
  team: {
    showTeamSection: boolean;
    hideWhenEmpty: boolean;
  };
  analytics?: {
    enabled: boolean;
    googleAnalyticsId: string;
    metaPixelId?: string;
  };
  chatWidget?: {
    enabled: boolean;
    webhookUrl: string;
    typingLabel?: string;
    title?: string;
  };
  paymentOverdueEmail?: {
    enabled: boolean;
    client: { subject: string; body: string };
    admin: { subject: string; body: string };
  };
}

interface SettingsContextType {
  settings: SystemSettings | null;
  loading: boolean;
  updateSettings: (newSettings: SystemSettings) => void;
  updateSettingsOnly: (newSettings: SystemSettings) => void;
  applyAppearanceSettings: () => void;
}

// Seções que a API pública NÃO devolve (só admins recebem). Defaults só para manter o shape do tipo.
const RESTRICTED_SETTINGS_DEFAULTS = {
  email: {
    provider: '',
    sendgridApiKey: '',
    mailersendApiKey: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    fromEmail: '',
    fromName: ''
  },
  database: { backupEnabled: true, backupFrequency: 'daily', retentionDays: 30 },
  security: { sessionTimeout: 24, maxLoginAttempts: 5, passwordMinLength: 8, twoFactorEnabled: false },
  notifications: { emailNotifications: true, adminNotifications: true, userNotifications: true }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const { isAdmin, loading: authLoading } = useAuth();

  // Visitantes recebem só a whitelist pública (GET /settings). Admins recebem a configuração
  // completa (GET /settings/admin, segredos mascarados) — refaz o fetch em login/logout.
  useEffect(() => {
    if (authLoading) return;
    fetchSettings(isAdmin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authLoading]);

  const fetchSettings = async (asAdmin: boolean) => {
    try {
      const response = await axios.get(asAdmin ? '/settings/admin' : '/settings');
      const data = response.data;
      setSettings({
        ...RESTRICTED_SETTINGS_DEFAULTS,
        ...data,
        chatWidget: data.chatWidget ?? { enabled: false, webhookUrl: '', typingLabel: 'IA digitando...', title: 'Atendimento NEIA' },
        paymentOverdueEmail: data.paymentOverdueEmail ?? {
          enabled: false,
          client: { subject: '', body: '' },
          admin: { subject: '', body: '' }
        }
      });
      
      // Verificar se as cores são rosa e forçar cores corretas
      const { primaryColor, secondaryColor } = data.appearance || {};
      const isPinkPrimary = primaryColor.includes('ec4899') || primaryColor.includes('d946ef') || 
                           primaryColor.includes('pink') || primaryColor.includes('rose');
      const isPinkSecondary = secondaryColor.includes('ec4899') || secondaryColor.includes('d946ef') || 
                             secondaryColor.includes('pink') || secondaryColor.includes('rose');
      
      applySiteBranding(data.site);

      if (isPinkPrimary || isPinkSecondary) {
        console.log('🚨 Cores rosa detectadas, forçando cores corretas...');
        forceThemeColors();
      } else {
        applyAppearanceSettings(data);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      // Usar configurações padrão em caso de erro
      const defaultSettings: SystemSettings = {
        site: {
          name: 'Europa Solutions',
          description: 'Sistema de imigração profissional',
          logo: '',
          favicon: '',
          language: 'pt-BR',
          timezone: 'America/Sao_Paulo',
          contact: { email: '', phone: '', address: '', businessHours: '' },
          geo: { latitude: '', longitude: '' }
        },
        email: {
          provider: '',
          sendgridApiKey: '',
          mailersendApiKey: '',
          smtpHost: '',
          smtpPort: 587,
          smtpUser: '',
          smtpPassword: '',
          smtpSecure: false,
          fromEmail: '',
          fromName: ''
        },
        database: {
          backupEnabled: true,
          backupFrequency: 'daily',
          retentionDays: 30
        },
        security: {
          sessionTimeout: 24,
          maxLoginAttempts: 5,
          passwordMinLength: 8,
          twoFactorEnabled: false
        },
        notifications: {
          emailNotifications: true,
          adminNotifications: true,
          userNotifications: true
        },
        social: {
          facebook: '',
          instagram: '',
          twitter: '',
          linkedin: '',
          youtube: '',
          tiktok: '',
          whatsapp: ''
        },
        appearance: {
          primaryColor: '#22c55e', // Verde do Brasil
          secondaryColor: '#ef4444', // Vermelho de Portugal
          theme: 'light',
          customCSS: ''
        },
        team: {
          showTeamSection: true,
          hideWhenEmpty: true
        },
        analytics: {
          enabled: false,
          googleAnalyticsId: ''
        },
        chatWidget: {
          enabled: false,
          webhookUrl: '',
          typingLabel: 'IA digitando...',
          title: 'Atendimento NEIA'
        }
      };
      setSettings(defaultSettings);
      forceThemeColors(); // Forçar cores corretas
    } finally {
      setLoading(false);
    }
  };

  const applySiteBranding = (site?: SystemSettings['site']) => {
    if (!site) return;
    applyFaviconToDocument(site.favicon);
  };

  const applyAppearanceSettings = (settingsData?: SystemSettings) => {
    const data = settingsData || settings;
    if (!data) return;

    applySiteBranding(data.site);

    const { appearance } = data;
    
    // Verificar se as cores são rosa e forçar cores corretas
    const isPinkPrimary = appearance.primaryColor.includes('ec4899') || appearance.primaryColor.includes('d946ef') || 
                         appearance.primaryColor.includes('pink') || appearance.primaryColor.includes('rose');
    const isPinkSecondary = appearance.secondaryColor.includes('ec4899') || appearance.secondaryColor.includes('d946ef') || 
                           appearance.secondaryColor.includes('pink') || appearance.secondaryColor.includes('rose');
    
    if (isPinkPrimary || isPinkSecondary) {
      console.log('🚨 Cores rosa detectadas em applyAppearanceSettings, forçando cores corretas...');
      forceThemeColors();
      return;
    }
    
    // Aplicar cores CSS customizadas
    const root = document.documentElement;
    root.style.setProperty('--primary-color', appearance.primaryColor);
    root.style.setProperty('--secondary-color', appearance.secondaryColor);
    
    // Aplicar tema
    if (appearance.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Aplicar CSS customizado
    let customStyleElement = document.getElementById('custom-appearance-styles');
    if (!customStyleElement) {
      customStyleElement = document.createElement('style');
      customStyleElement.id = 'custom-appearance-styles';
      document.head.appendChild(customStyleElement);
    }
    
    customStyleElement.textContent = `
      :root {
        --primary-color: ${appearance.primaryColor};
        --secondary-color: ${appearance.secondaryColor};
        --primary-50: ${adjustColor(appearance.primaryColor, 200)};
        --primary-100: ${adjustColor(appearance.primaryColor, 150)};
        --primary-200: ${adjustColor(appearance.primaryColor, 100)};
        --primary-300: ${adjustColor(appearance.primaryColor, 50)};
        --primary-400: ${adjustColor(appearance.primaryColor, 20)};
        --primary-500: ${appearance.primaryColor};
        --primary-600: ${adjustColor(appearance.primaryColor, -20)};
        --primary-700: ${adjustColor(appearance.primaryColor, -40)};
        --primary-800: ${adjustColor(appearance.primaryColor, -60)};
        --primary-900: ${adjustColor(appearance.primaryColor, -80)};
        
        --secondary-50: ${adjustColor(appearance.secondaryColor, 200)};
        --secondary-100: ${adjustColor(appearance.secondaryColor, 150)};
        --secondary-200: ${adjustColor(appearance.secondaryColor, 100)};
        --secondary-300: ${adjustColor(appearance.secondaryColor, 50)};
        --secondary-400: ${adjustColor(appearance.secondaryColor, 20)};
        --secondary-500: ${appearance.secondaryColor};
        --secondary-600: ${adjustColor(appearance.secondaryColor, -20)};
        --secondary-700: ${adjustColor(appearance.secondaryColor, -40)};
        --secondary-800: ${adjustColor(appearance.secondaryColor, -60)};
        --secondary-900: ${adjustColor(appearance.secondaryColor, -80)};
      }
      
      /* Cores Primárias - Backgrounds */
      .bg-primary-50 { background-color: var(--primary-50) !important; }
      .bg-primary-100 { background-color: var(--primary-100) !important; }
      .bg-primary-200 { background-color: var(--primary-200) !important; }
      .bg-primary-300 { background-color: var(--primary-300) !important; }
      .bg-primary-400 { background-color: var(--primary-400) !important; }
      .bg-primary-500 { background-color: var(--primary-500) !important; }
      .bg-primary-600 { background-color: var(--primary-600) !important; }
      .bg-primary-700 { background-color: var(--primary-700) !important; }
      .bg-primary-800 { background-color: var(--primary-800) !important; }
      .bg-primary-900 { background-color: var(--primary-900) !important; }
      
      /* Cores Primárias - Textos */
      .text-primary-50 { color: var(--primary-50) !important; }
      .text-primary-100 { color: var(--primary-100) !important; }
      .text-primary-200 { color: var(--primary-200) !important; }
      .text-primary-300 { color: var(--primary-300) !important; }
      .text-primary-400 { color: var(--primary-400) !important; }
      .text-primary-500 { color: var(--primary-500) !important; }
      .text-primary-600 { color: var(--primary-600) !important; }
      .text-primary-700 { color: var(--primary-700) !important; }
      .text-primary-800 { color: var(--primary-800) !important; }
      .text-primary-900 { color: var(--primary-900) !important; }
      
      /* Cores Primárias - Bordas */
      .border-primary-50 { border-color: var(--primary-50) !important; }
      .border-primary-100 { border-color: var(--primary-100) !important; }
      .border-primary-200 { border-color: var(--primary-200) !important; }
      .border-primary-300 { border-color: var(--primary-300) !important; }
      .border-primary-400 { border-color: var(--primary-400) !important; }
      .border-primary-500 { border-color: var(--primary-500) !important; }
      .border-primary-600 { border-color: var(--primary-600) !important; }
      .border-primary-700 { border-color: var(--primary-700) !important; }
      .border-primary-800 { border-color: var(--primary-800) !important; }
      .border-primary-900 { border-color: var(--primary-900) !important; }
      
      /* Cores Secundárias - Backgrounds */
      .bg-secondary-50 { background-color: var(--secondary-50) !important; }
      .bg-secondary-100 { background-color: var(--secondary-100) !important; }
      .bg-secondary-200 { background-color: var(--secondary-200) !important; }
      .bg-secondary-300 { background-color: var(--secondary-300) !important; }
      .bg-secondary-400 { background-color: var(--secondary-400) !important; }
      .bg-secondary-500 { background-color: var(--secondary-500) !important; }
      .bg-secondary-600 { background-color: var(--secondary-600) !important; }
      .bg-secondary-700 { background-color: var(--secondary-700) !important; }
      .bg-secondary-800 { background-color: var(--secondary-800) !important; }
      .bg-secondary-900 { background-color: var(--secondary-900) !important; }
      
      /* Cores Secundárias - Textos */
      .text-secondary-50 { color: var(--secondary-50) !important; }
      .text-secondary-100 { color: var(--secondary-100) !important; }
      .text-secondary-200 { color: var(--secondary-200) !important; }
      .text-secondary-300 { color: var(--secondary-300) !important; }
      .text-secondary-400 { color: var(--secondary-400) !important; }
      .text-secondary-500 { color: var(--secondary-500) !important; }
      .text-secondary-600 { color: var(--secondary-600) !important; }
      .text-secondary-700 { color: var(--secondary-700) !important; }
      .text-secondary-800 { color: var(--secondary-800) !important; }
      .text-secondary-900 { color: var(--secondary-900) !important; }
      
      /* Cores Secundárias - Bordas */
      .border-secondary-50 { border-color: var(--secondary-50) !important; }
      .border-secondary-100 { border-color: var(--secondary-100) !important; }
      .border-secondary-200 { border-color: var(--secondary-200) !important; }
      .border-secondary-300 { border-color: var(--secondary-300) !important; }
      .border-secondary-400 { border-color: var(--secondary-400) !important; }
      .border-secondary-500 { border-color: var(--secondary-500) !important; }
      .border-secondary-600 { border-color: var(--secondary-600) !important; }
      .border-secondary-700 { border-color: var(--secondary-700) !important; }
      .border-secondary-800 { border-color: var(--secondary-800) !important; }
      .border-secondary-900 { border-color: var(--secondary-900) !important; }
      
      /* Hover States */
      .hover\\:bg-primary-50:hover { background-color: var(--primary-50) !important; }
      .hover\\:bg-primary-100:hover { background-color: var(--primary-100) !important; }
      .hover\\:bg-primary-200:hover { background-color: var(--primary-200) !important; }
      .hover\\:bg-primary-300:hover { background-color: var(--primary-300) !important; }
      .hover\\:bg-primary-400:hover { background-color: var(--primary-400) !important; }
      .hover\\:bg-primary-500:hover { background-color: var(--primary-500) !important; }
      .hover\\:bg-primary-600:hover { background-color: var(--primary-600) !important; }
      .hover\\:bg-primary-700:hover { background-color: var(--primary-700) !important; }
      .hover\\:bg-primary-800:hover { background-color: var(--primary-800) !important; }
      .hover\\:bg-primary-900:hover { background-color: var(--primary-900) !important; }
      
      .hover\\:text-primary-50:hover { color: var(--primary-50) !important; }
      .hover\\:text-primary-100:hover { color: var(--primary-100) !important; }
      .hover\\:text-primary-200:hover { color: var(--primary-200) !important; }
      .hover\\:text-primary-300:hover { color: var(--primary-300) !important; }
      .hover\\:text-primary-400:hover { color: var(--primary-400) !important; }
      .hover\\:text-primary-500:hover { color: var(--primary-500) !important; }
      .hover\\:text-primary-600:hover { color: var(--primary-600) !important; }
      .hover\\:text-primary-700:hover { color: var(--primary-700) !important; }
      .hover\\:text-primary-800:hover { color: var(--primary-800) !important; }
      .hover\\:text-primary-900:hover { color: var(--primary-900) !important; }
      
      /* Focus States */
      .focus\\:ring-primary-500:focus { --tw-ring-color: var(--primary-500) !important; }
      .focus\\:ring-primary-600:focus { --tw-ring-color: var(--primary-600) !important; }
      .focus\\:border-primary-500:focus { border-color: var(--primary-500) !important; }
      .focus\\:border-primary-600:focus { border-color: var(--primary-600) !important; }
      
      /* Gradientes para textos */
      .bg-gradient-to-r.from-primary-500.to-secondary-500 { 
        background: linear-gradient(to right, var(--primary-500), var(--secondary-500)) !important; 
      }
      .bg-gradient-to-r.from-primary-600.to-secondary-600 { 
        background: linear-gradient(to right, var(--primary-600), var(--secondary-600)) !important; 
      }
      
      /* Gradientes para backgrounds */
      .bg-gradient-to-br.from-primary-50.via-white.to-secondary-50 {
        background: linear-gradient(to bottom right, var(--primary-50), white, var(--secondary-50)) !important;
      }
      
      /* Gradientes para textos com clip */
      .text-transparent.bg-clip-text.bg-gradient-to-r.from-primary-600.to-secondary-600 {
        background: linear-gradient(to right, var(--primary-600), var(--secondary-600)) !important;
        -webkit-background-clip: text !important;
        background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        color: transparent !important;
      }
      
      /* Gradientes para textos com clip - variações */
      .text-transparent.bg-clip-text.bg-gradient-to-r.from-primary-500.to-secondary-500 {
        background: linear-gradient(to right, var(--primary-500), var(--secondary-500)) !important;
        -webkit-background-clip: text !important;
        background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        color: transparent !important;
      }
      
      /* Animações removidas para evitar efeitos indesejados */
      
      /* Botões dinâmicos */
      .btn-primary {
        background-color: var(--primary-600) !important;
        color: white !important;
        border-color: var(--primary-600) !important;
      }
      .btn-primary:hover {
        background-color: var(--primary-700) !important;
        border-color: var(--primary-700) !important;
      }
      
      .btn-secondary {
        background-color: var(--secondary-600) !important;
        color: white !important;
        border-color: var(--secondary-600) !important;
      }
      .btn-secondary:hover {
        background-color: var(--secondary-700) !important;
        border-color: var(--secondary-700) !important;
      }
      
      .btn-outline {
        background-color: transparent !important;
        color: var(--primary-600) !important;
        border-color: var(--primary-600) !important;
      }
      .btn-outline:hover {
        background-color: var(--primary-600) !important;
        color: white !important;
      }
      
      ${appearance.customCSS}
    `;
  };

  const adjustColor = (color: string, amount: number) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const newR = Math.max(0, Math.min(255, r + amount));
    const newG = Math.max(0, Math.min(255, g + amount));
    const newB = Math.max(0, Math.min(255, b + amount));
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  const updateSettings = (newSettings: SystemSettings) => {
    setSettings(newSettings);
    applySiteBranding(newSettings.site);
    applyAppearanceSettings(newSettings);
  };

  const updateSettingsOnly = (newSettings: SystemSettings) => {
    setSettings(newSettings);
    // Não aplica configurações de aparência - apenas atualiza o estado
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      loading,
      updateSettings,
      updateSettingsOnly,
      applyAppearanceSettings
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
